import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Bot, History as HistoryIcon, MessageSquare, Search, X } from "lucide-react";
import { Container, Row, Card, Button, Form, Navbar } from "react-bootstrap";
import "./mcp.css";
import axios from "../../axios";
import apiError from "../../error";
import { SOCKET_EVENTS, socket } from "../../socket";
import LoadingDots from "../../component/dots-loader/dots-loader";
import { PiSparkleBold } from "react-icons/pi";
import AiChatMessageBody, {
    extractCrpPreviewId,
    aiMessageShouldUseFullWidth,
} from "../../component/chat/ai-chat-message-body";
import { messageLooksLikeAiChart } from "../../component/chat/ai-html-utils";
import GroupListSelection from "../../component/ai-components/group-list-selection";
import AiSimulationAttack from "../../component/ai-components/ai-simulation-attack";
import AiTemplateSelection from "../../component/a-components/ai-template-selection";
import Coin from "../../assets/coin.png";
import ThreatcopAiIcon from "../../assets/threatcop-ai-icon.png";
import { PiArrowUpBold } from "react-icons/pi";
import { PiArrowSquareOut } from "react-icons/pi";
import { PiPlus } from "react-icons/pi";
import { useLocation } from "react-router-dom";
import { FaStop } from "react-icons/fa";
import {
    createAwarenessManagerPath,
    getLocalChatSessions,
    getOrCreateSessionId,
    getSessionHistory,
    saveSessionHistory,
    setStoredSessionId,
} from "./session";
import AiCampaignType from "../../component/ai-components/ai-campaign-type";
import CampaignDraftPreview from "../../component/ai-components/campaign-draft-preview";
import TemplatePreview from "../../component/ai-components/campaign-steps/preview/preview";
import AiCampaignTemplate from "../../component/ai-components/ai-campaign-template";
import AiCreateGroup from "../../component/ai-components/ai-create-group";
import TemplateCreationPrompt from "../../component/ai-components/template-creation-prompt";
import customSwal from "../../customSwal";

const CHAT_HISTORY_LIMIT = 7;
const DEFAULT_RIGHT_PANEL_WIDTH = 700;
const DEFAULT_RIGHT_PANEL_MIN_WIDTH = 520;
const DEFAULT_RIGHT_PANEL_MAX_WIDTH = 900;
const TEMPLATE_RIGHT_PANEL_WIDTH = 800;
const TEMPLATE_RIGHT_PANEL_MIN_WIDTH = 520;
const TEMPLATE_RIGHT_PANEL_MAX_WIDTH = 1000;
const RIGHT_PANEL_VIEWPORT_GUTTER = 112;
const MIN_LOADING_PLACEHOLDER_MS = 650;

function clampPanelWidth(width, minWidth, maxWidth) {
    return Math.min(maxWidth, Math.max(minWidth, width));
}

function getViewportPanelMaxWidth(fallbackMaxWidth, minWidth) {
    if (typeof window === "undefined") return fallbackMaxWidth;

    return Math.max(
        minWidth,
        Math.min(fallbackMaxWidth, window.innerWidth - RIGHT_PANEL_VIEWPORT_GUTTER)
    );
}

/** Matches `CLP_` + 10 alphanumeric chars (campaign launch preview id, e.g. CLP_0561398419). */
const CLP_PREVIEW_PATTERN = /CLP_[A-Za-z0-9]{10}/;

function extractClpPreviewId(content) {
    if (typeof content !== "string") return null;
    const m = content.match(CLP_PREVIEW_PATTERN);
    return m ? m[0] : null;
}

/** Removes the CLP launch-preview id(s) from the text so it isn't shown in the reply. */
function stripClpPreviewId(content) {
    if (typeof content !== "string") return content;
    return content
        .replace(new RegExp(CLP_PREVIEW_PATTERN.source, "g"), "")
        .replace(/\s+/g, " ")
        .trim();
}

/** Matches `CTP_` + the numeric campaign-template id (campaign template preview id, e.g. CTP_1234). */
const CTP_PREVIEW_PATTERN = /CTP_(\d+)/;

/** Returns the numeric campaign template id from a CTP_ token, or null. */
function extractCtpPreviewId(content) {
    if (typeof content !== "string") return null;
    const m = content.match(CTP_PREVIEW_PATTERN);
    return m ? Number(m[1]) : null;
}

/** Removes the CTP template-preview token(s) from the text so it isn't shown in the reply. */
function stripCtpPreviewId(content) {
    if (typeof content !== "string") return content;
    return content
        .replace(new RegExp(CTP_PREVIEW_PATTERN.source, "g"), "")
        .replace(/\s+/g, " ")
        .trim();
}

function isUsableSessionId(value) {
    return !!value && value !== "null" && value !== "undefined";
}

function cleanHistoryText(value) {
    return String(value || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function getHistoryPreviewText(value) {
    return extractCrpPreviewId(value) ? "Custom Report Preview" : cleanHistoryText(value);
}

function normalizeChatMessages(messages = []) {
    if (!Array.isArray(messages)) return [];

    return messages
        .filter((message) => ["user", "ai"].includes(message?.role) && typeof message?.content === "string")
        .map((message) => ({
            role: message.role,
            content: message.content,
        }));
}

function formatHistoryDate(value) {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    return date.toLocaleString(
        undefined,
        isToday
            ? { hour: "2-digit", minute: "2-digit" }
            : { month: "short", day: "numeric" }
    );
}

function mergeChatSessions(remoteSessions = [], localSessions = []) {
    const sessionsById = new Map();

    localSessions.forEach((session) => {
        if (!isUsableSessionId(session?.sessionId)) return;
        sessionsById.set(session.sessionId, {
            ...session,
            source: "local",
        });
    });

    remoteSessions.forEach((session) => {
        if (!isUsableSessionId(session?.sessionId)) return;

        const existing = sessionsById.get(session.sessionId) || {};

        sessionsById.set(session.sessionId, {
            ...existing,
            ...session,
            preview: existing.preview || session.preview || "",
            source: "remote",
        });
    });

    return Array.from(sessionsById.values())
        .sort((a, b) => {
            return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
        })
        .slice(0, CHAT_HISTORY_LIMIT);
}

function useStreamBuffer(content, isStreaming) {
    const bufferRef = useRef("");
    const [visibleContent, setVisibleContent] = useState("");

    useEffect(() => {
        if (!isStreaming) {
            bufferRef.current = "";
            setVisibleContent(content || "");
            return;
        }

        bufferRef.current = content || "";
        const safe = extractSafeContent(bufferRef.current);
        setVisibleContent(safe);
    }, [content, isStreaming]);

    return visibleContent;
}

function extractSafeContent(text) {
    if (!text || typeof text !== "string") return "";

    const looksLikeChart = messageLooksLikeAiChart(text);

    if (!text.includes("<")) {
        if (looksLikeChart) {
            const cut = text.search(/\*\/\s*\(?\s*function\s+render/i);
            if (cut > 0) return text.slice(0, cut).trim();
            if (cut === 0) return "";
        }

        const lastNewline = text.lastIndexOf("\n");
        if (lastNewline > 0) return text.slice(0, lastNewline);

        return text;
    }

    const closingTagRegex = /<\/[a-zA-Z][\w-]*\s*>/g;
    let lastEnd = -1;
    let match;

    while ((match = closingTagRegex.exec(text)) !== null) {
        lastEnd = match.index + match[0].length;
    }

    if (lastEnd === -1) return "";

    const safe = text.slice(0, lastEnd);

    if (looksLikeChart) {
        const orphanStart = text.slice(lastEnd).search(/\*\/\s*\(?\s*function\s+render/i);
        if (orphanStart === 0) {
            return safe;
        }
    }

    return safe;
}

function containsHTML(text) {
    if (typeof text !== "string") return false;

    const lower = text.toLowerCase();

    if (lower.includes("&lt;table") || lower.includes("&lt;div") || lower.includes("&lt;script")) {
        return true;
    }

    if (!text.includes("<") || !text.includes(">")) return false;

    const htmlTagRegex = /<\/?[a-z][\s\S]*?>/i;
    return htmlTagRegex.test(text);
}

function decodeHtmlEntities(value) {
    if (typeof value !== "string") return value;

    if (typeof document === "undefined") {
        return value
            .replace(/&quot;/g, '"')
            .replace(/&#34;/g, '"')
            .replace(/&apos;/g, "'")
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">");
    }

    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
}

function deepDecodeStrings(value) {
    if (typeof value === "string") {
        let decoded = value;

        // Handles double/triple encoded values like:
        // "Plus1 &amp;amp; 2" -> "Plus1 & 2"
        for (let i = 0; i < 3; i += 1) {
            const next = decodeHtmlEntities(decoded);
            if (next === decoded) break;
            decoded = next;
        }

        return decoded;
    }

    if (Array.isArray(value)) {
        return value.map(deepDecodeStrings);
    }

    if (value && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value).map(([key, val]) => [key, deepDecodeStrings(val)])
        );
    }

    return value;
}

function safeJsonParse(value) {
    try {
        return deepDecodeStrings(JSON.parse(value));
    } catch (error) {
        return null;
    }
}

/**
 * Tries to parse an AI message content as structured JSON.
 *
 * Supports:
 * 1. Raw JSON:
 *    { "jsonId": "fetch-group-list-to-select" }
 *
 * 2. Markdown JSON:
 *    ```json
 *    { ... }
 *    ```
 *
 * 3. HTML wrapped JSON:
 *    <pre><code class="language-json">{ ... }</code></pre>
 *
 * 4. Mixed HTML + JSON + HTML:
 *    <h3>Before</h3>
 *    <pre><code class="language-json">{ ... }</code></pre>
 *    <h3>After</h3>
 */
function tryParseAiJson(content) {
    if (!content || typeof content !== "string") {
        return {
            json: null,
            beforeHtml: "",
            afterHtml: "",
            rawJson: null,
        };
    }

    const trimmed = content.trim();

    // Case 1: direct raw JSON
    if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
        const parsed = safeJsonParse(trimmed);

        if (parsed) {
            return {
                json: parsed,
                beforeHtml: "",
                afterHtml: "",
                rawJson: trimmed,
            };
        }
    }

    // Case 2: markdown code block
    const markdownCodeBlockMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);

    if (markdownCodeBlockMatch) {
        const rawJson = markdownCodeBlockMatch[1].trim();
        const parsed = safeJsonParse(rawJson);

        if (parsed) {
            const beforeHtml = trimmed.slice(0, markdownCodeBlockMatch.index).trim();
            const afterHtml = trimmed
                .slice(markdownCodeBlockMatch.index + markdownCodeBlockMatch[0].length)
                .trim();

            return {
                json: parsed,
                beforeHtml,
                afterHtml,
                rawJson,
            };
        }
    }

    // Case 3: HTML <pre><code> JSON block inside normal HTML
    if (typeof DOMParser !== "undefined") {
        try {
            const doc = new DOMParser().parseFromString(content, "text/html");
            const codeBlocks = Array.from(doc.querySelectorAll("pre code"));

            const jsonCodeEl = codeBlocks.find((codeEl) => {
                const codeText = codeEl.textContent?.trim() || "";
                const className = codeEl.className || "";

                return (
                    className.toLowerCase().includes("language-json") ||
                    codeText.startsWith("{") ||
                    codeText.startsWith("[")
                );
            });

            if (jsonCodeEl) {
                const preEl = jsonCodeEl.closest("pre");
                const rawJson = jsonCodeEl.textContent?.trim() || "";
                const parsed = safeJsonParse(rawJson);

                if (preEl && parsed) {
                    const marker = `__MCP_JSON_BLOCK_${Date.now()}_${Math.random()}__`;

                    preEl.replaceWith(doc.createTextNode(marker));

                    const htmlWithoutJsonBlock = doc.body.innerHTML;
                    const markerIndex = htmlWithoutJsonBlock.indexOf(marker);

                    return {
                        json: parsed,
                        beforeHtml:
                            markerIndex >= 0
                                ? htmlWithoutJsonBlock.slice(0, markerIndex).trim()
                                : "",
                        afterHtml:
                            markerIndex >= 0
                                ? htmlWithoutJsonBlock.slice(markerIndex + marker.length).trim()
                                : "",
                        rawJson,
                    };
                }
            }
        } catch (error) {
            return {
                json: null,
                beforeHtml: content,
                afterHtml: "",
                rawJson: null,
            };
        }
    }

    if (typeof DOMParser !== "undefined") {
        try {
            const doc = new DOMParser().parseFromString(content, "text/html");
            const paragraphs = Array.from(doc.querySelectorAll("p"));

            const jsonPara = paragraphs.find((p) => {
                const text = p.textContent?.trim() || "";
                return (text.startsWith("{") && text.endsWith("}")) ||
                    (text.startsWith("[") && text.endsWith("]"));
            });

            if (jsonPara) {
                const rawJson = jsonPara.textContent?.trim() || "";
                const parsed = safeJsonParse(rawJson);

                if (parsed) {
                    const marker = `__MCP_JSON_BLOCK_${Date.now()}_${Math.random()}__`;
                    jsonPara.replaceWith(doc.createTextNode(marker));
                    const htmlWithoutJsonBlock = doc.body.innerHTML;
                    const markerIndex = htmlWithoutJsonBlock.indexOf(marker);

                    return {
                        json: parsed,
                        beforeHtml: markerIndex >= 0 ? htmlWithoutJsonBlock.slice(0, markerIndex).trim() : "",
                        afterHtml: markerIndex >= 0 ? htmlWithoutJsonBlock.slice(markerIndex + marker.length).trim() : "",
                        rawJson,
                    };
                }
            }
        } catch (error) {}
    }

    return {
        json: null,
        beforeHtml: content,
        afterHtml: "",
        rawJson: null,
    };
}

function AiMessageRenderer({ content, isStreaming }) {
    const visibleContent = useStreamBuffer(content, isStreaming);
    const displayContent = isStreaming ? visibleContent : content || "";

    if (!displayContent) return null;

    if (
        !isStreaming ||
        containsHTML(displayContent) ||
        messageLooksLikeAiChart(displayContent) ||
        extractCrpPreviewId(displayContent)
    ) {
        return (
            <AiChatMessageBody
                content={displayContent}
                isStreaming={isStreaming}
            />
        );
    }

    return (
        <div
            className="mcp-message-html mcp-streaming-plain"
            style={{
                width: "100%",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
            }}
        >
            {displayContent}
        </div>
    );
}

export default function MCP({
    location,
    match,
    history: routerHistory,
    onRegisterNewChat,
    onRegisterOpenHistory,
    onCollapseSidebar,
    onRegisterSidebarBridge,
}) {
    const isAIReport =
        new URLSearchParams(location?.search || "").get("isAIReport") === "true" ||
        new URLSearchParams(location?.search || "").get("isAIReport") === "1";

    const locationHook = useLocation();
    const params = new URLSearchParams(locationHook.search);
    const routeSessionId = params.get("sessionId");

    const initialSessionId = routeSessionId || "";
    const chatType = isAIReport ? "report" : "default";

    const [sessionId, setSessionId] = useState(initialSessionId);
    const [history, setHistory] = useState(() =>
        isUsableSessionId(initialSessionId)
            ? getSessionHistory(initialSessionId)
            : []
    );
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showLoadingPlaceholder, setShowLoadingPlaceholder] = useState(false);
    const [_tokenUsed, setTokenUsed] = useState(0);
    const [tokenLeft, setTokenLeft] = useState(0);
    const [isBotReplying, setIsBotReplying] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [chatSessions, setChatSessions] = useState(() => getLocalChatSessions());
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState("");
    const [loadingSessionId, setLoadingSessionId] = useState("");
    const [historySearch, setHistorySearch] = useState("");
    const [hasMoreChatSessions, setHasMoreChatSessions] = useState(false);

    const bottomRef = useRef(null);
    const latestUserMessageRef = useRef(null);
    const loadingPlaceholderTimerRef = useRef(null);
    const loadingPlaceholderStartedAtRef = useRef(0);
    const holdUserMessageAtTopUntilRef = useRef(0);
    const pendingUserMessageScrollRef = useRef(false);
    const skipNextHistoryTouchRef = useRef(false);
    const messageInputRef = useRef(null);
    const shouldFocusInputAfterSendRef = useRef(false);

    const focusMessageInput = useCallback(() => {
        requestAnimationFrame(() => {
            const inputEl = messageInputRef.current;
            if (!inputEl) return;

            inputEl.focus();
            const nextPosition = inputEl.value.length;
            inputEl.setSelectionRange(nextPosition, nextPosition);
        });
    }, []);

    const clearLoadingPlaceholderTimer = useCallback(() => {
        if (loadingPlaceholderTimerRef.current) {
            clearTimeout(loadingPlaceholderTimerRef.current);
            loadingPlaceholderTimerRef.current = null;
        }
    }, []);

    const showLoadingPlaceholderForMinimumDuration = useCallback(() => {
        const now = Date.now();

        clearLoadingPlaceholderTimer();
        loadingPlaceholderStartedAtRef.current = now;
        holdUserMessageAtTopUntilRef.current = now + MIN_LOADING_PLACEHOLDER_MS;
        setShowLoadingPlaceholder(true);
    }, [clearLoadingPlaceholderTimer]);

    const hideLoadingPlaceholderAfterMinimumDuration = useCallback(() => {
        const startedAt = loadingPlaceholderStartedAtRef.current;

        if (!startedAt) {
            setShowLoadingPlaceholder(false);
            return;
        }

        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(MIN_LOADING_PLACEHOLDER_MS - elapsed, 0);

        clearLoadingPlaceholderTimer();
        loadingPlaceholderTimerRef.current = setTimeout(() => {
            setShowLoadingPlaceholder(false);
            loadingPlaceholderStartedAtRef.current = 0;
            holdUserMessageAtTopUntilRef.current = 0;
            loadingPlaceholderTimerRef.current = null;
        }, remaining);
    }, [clearLoadingPlaceholderTimer]);

    const resetLoadingPlaceholder = useCallback(() => {
        clearLoadingPlaceholderTimer();
        loadingPlaceholderStartedAtRef.current = 0;
        holdUserMessageAtTopUntilRef.current = 0;
        pendingUserMessageScrollRef.current = false;
        setShowLoadingPlaceholder(false);
    }, [clearLoadingPlaceholderTimer]);

    useEffect(() => () => clearLoadingPlaceholderTimer(), [clearLoadingPlaceholderTimer]);

    const setLoadedHistory = useCallback((nextHistory) => {
        skipNextHistoryTouchRef.current = true;
        setHistory(nextHistory);
    }, []);

    const loadConversationBySessionId = useCallback(async (nextSessionId, options = {}) => {
        if (!isUsableSessionId(nextSessionId)) return [];

        const fallbackHistory = options.fallbackHistory || getSessionHistory(nextSessionId);
        setLoadingSessionId(nextSessionId);

        if (fallbackHistory.length) {
            setLoadedHistory(fallbackHistory);
        }

        try {
            const response = await axios.get(
                `/api/awareness-manager/history/${encodeURIComponent(nextSessionId)}`,
                {
                    params: { type: chatType },
                }
            );

            const serverHistory = normalizeChatMessages(response.data?.messages);

            if (serverHistory.length) {
                setHistoryError("");
                setLoadedHistory(serverHistory);
                saveSessionHistory(nextSessionId, serverHistory, {
                    touch: false,
                    createdAt: response.data?.conversation?.createdAt,
                    updatedAt: response.data?.conversation?.updatedAt,
                });
                return serverHistory;
            }

            return fallbackHistory;
        } catch (err) {
            if (!options.silent) {
                setHistoryError("Could not open the saved chat. Showing the local copy if one exists.");
            }

            if (!fallbackHistory.length) {
                setLoadedHistory([]);
            }

            return fallbackHistory;
        } finally {
            setLoadingSessionId("");
        }
    }, [chatType, setLoadedHistory]);

    const loadChatSessions = useCallback(async () => {
        const localSessions = getLocalChatSessions();

        setChatSessions(mergeChatSessions([], localSessions));
        setHasMoreChatSessions(false);
        setIsHistoryLoading(true);
        setHistoryError("");

        try {
            const response = await axios.get("/api/awareness-manager/history", {
                params: { type: chatType, limit: CHAT_HISTORY_LIMIT },
            });

            const serverSessions = Array.isArray(response.data?.conversations)
                ? response.data.conversations
                : [];

            setChatSessions(mergeChatSessions(serverSessions, localSessions));
            setHasMoreChatSessions(!!response.data?.hasMore);
        } catch (err) {
            setHistoryError("Saved chat history is not reachable right now. Showing chats saved in this browser.");
            setChatSessions(mergeChatSessions([], localSessions));
            setHasMoreChatSessions(false);
        } finally {
            setIsHistoryLoading(false);
        }
    }, [chatType]);

    useEffect(() => {
        loadChatSessions();
    }, [loadChatSessions]);

    useEffect(() => {
        if (isUsableSessionId(sessionId)) {
            saveSessionHistory(sessionId, history, {
                touch: !skipNextHistoryTouchRef.current,
            });
        }

        skipNextHistoryTouchRef.current = false;
    }, [history, sessionId]);

    useEffect(() => {
        const nextSessionId = isUsableSessionId(routeSessionId) ? routeSessionId : "";

        setStoredSessionId(nextSessionId);
        setSessionId(nextSessionId);
        setLoadedHistory(nextSessionId ? getSessionHistory(nextSessionId) : []);
        setInput("");
        setIsLoading(false);
        setIsBotReplying(false);
        resetLoadingPlaceholder();

        if (nextSessionId) {
            loadConversationBySessionId(nextSessionId, {
                fallbackHistory: getSessionHistory(nextSessionId),
                silent: true,
            });
        }
    }, [routeSessionId, loadConversationBySessionId, resetLoadingPlaceholder, setLoadedHistory]);

    useEffect(() => {
        if (isHistoryOpen) {
            loadChatSessions();
        }
    }, [isHistoryOpen, loadChatSessions]);

    useEffect(() => {
        if (!isHistoryOpen) return undefined;

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsHistoryOpen(false);
            }
        };

        window.addEventListener("keydown", handleEscape);

        return () => window.removeEventListener("keydown", handleEscape);
    }, [isHistoryOpen]);

    useEffect(() => {
        if (pendingUserMessageScrollRef.current && latestUserMessageRef.current) {
            pendingUserMessageScrollRef.current = false;
            const rafId = requestAnimationFrame(() => {
                if (latestUserMessageRef.current) {
                    latestUserMessageRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            });

            return () => cancelAnimationFrame(rafId);
        }

        if (Date.now() < holdUserMessageAtTopUntilRef.current) return undefined;

        if (bottomRef.current && !isBotReplying) {
            bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }

        return undefined;
    }, [history, isLoading, isBotReplying]);

    useEffect(() => {
        if (!isLoading && showLoadingPlaceholder) {
            hideLoadingPlaceholderAfterMinimumDuration();
        }
    }, [isLoading, showLoadingPlaceholder, hideLoadingPlaceholderAfterMinimumDuration]);

    const startNewChat = () => {
        setStoredSessionId(null);
        setSessionId(null);
        setHistory([]);
        setTokenUsed(0);
        setTokenLeft(0);
        setIsLoading(false);
        setIsBotReplying(false);
        setIsHistoryOpen(false);
        resetLoadingPlaceholder();

        if (isAIReport) routerHistory.push(`/awareness-manager?isAIReport=${isAIReport}`);
        else routerHistory.push("/awareness-manager");
    };

    useEffect(() => {
        if (onRegisterNewChat) onRegisterNewChat(startNewChat);
        // eslint-disable-next-line
    }, [onRegisterNewChat]);

    useEffect(() => {
        if (onRegisterOpenHistory) onRegisterOpenHistory(() => setIsHistoryOpen(true));
        // eslint-disable-next-line
    }, [onRegisterOpenHistory]);

    const openChatSession = useCallback(async (nextSessionId) => {
        if (!isUsableSessionId(nextSessionId) || isLoading || isBotReplying) return;

        const fallbackHistory = getSessionHistory(nextSessionId);

        setStoredSessionId(nextSessionId);
        setSessionId(nextSessionId);
        setInput("");
        setHistoryError("");
        setIsLoading(false);
        setIsBotReplying(false);
        resetLoadingPlaceholder();
        setLoadedHistory(fallbackHistory);
        updateQueryParams({ sessionId: nextSessionId });

        await loadConversationBySessionId(nextSessionId, {
            fallbackHistory,
            silent: false,
        });

        setIsHistoryOpen(false);
    }, [isLoading, isBotReplying, loadConversationBySessionId, resetLoadingPlaceholder, setLoadedHistory]);

    useEffect(() => {
        if (onRegisterSidebarBridge) {
            onRegisterSidebarBridge({
                sessions: chatSessions,
                loadSessions: loadChatSessions,
                openSession: openChatSession,
                currentSessionId: sessionId,
                cleanTitle: (title) => cleanHistoryText(title) || "New Conversation",
            });
        }
        // eslint-disable-next-line
    }, [chatSessions, sessionId]);

    const sendMessage = async (e) => {
        if (typeof e === "string") {
            setInput(e);
        } else if (e) {
            e.preventDefault();
        }

        const userInput = typeof e === "string" ? e : input;

        if (!userInput.trim()) return;
        if (isLoading || isBotReplying) {
            focusMessageInput();
            return;
        }

        if (!socket.connected) socket.connect();

        const sanitizedHistory = history.map((h) => ({
            role: h.role,
            content: h.content,
        }));

        const newHistory = [
            ...sanitizedHistory,
            {
                role: "user",
                content: userInput,
            },
        ];

        pendingUserMessageScrollRef.current = true;
        shouldFocusInputAfterSendRef.current = true;
        showLoadingPlaceholderForMinimumDuration();
        setHistory(newHistory);
        setIsLoading(true);
        setInput("");
        focusMessageInput();

        try {
            const requestSessionId = isUsableSessionId(sessionId) ? sessionId : "null";

            const response = await axios.post(`/api/awareness-manager/${requestSessionId}`, {
                messages: newHistory,
                isAIReport,
            });

            if (!isUsableSessionId(sessionId)) {
                updateQueryParams({ sessionId: response.data.sessionId });
                setStoredSessionId(response.data.sessionId);
                setSessionId(response.data.sessionId);
            }
        } catch (err) {
            apiError(err);
            setIsLoading(false);
            setIsBotReplying(false);
        }
    };

    useEffect(() => {
        if (isLoading || isBotReplying || !shouldFocusInputAfterSendRef.current) return;

        shouldFocusInputAfterSendRef.current = false;
        focusMessageInput();
    }, [isLoading, isBotReplying, focusMessageInput]);

    const updateQueryParams = (paramsObj, options = {}) => {
        const url = new URL(window.location.href);

        Object.entries(paramsObj).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                url.searchParams.delete(key);
            } else {
                url.searchParams.set(key, value);
            }
        });

        if (options.replace) {
            window.history.replaceState({}, "", url);
        } else {
            window.history.pushState({}, "", url);
        }
    };

    const getTokenUsed = async () => {
        try {
            const response = await axios.get("/api/awareness-manager");

            setTokenUsed(response.data.tokenUsed || 0);
            setTokenLeft(response.data.leftTokens || 0);
        } catch (err) {
            apiError(err);
        }
    };

    const getMessage = useCallback((msg) => {
        if (msg?.sessionId && msg.sessionId !== sessionId) {
            return;
        }

        if (msg?.status === "stopped") {
            setIsLoading(false);
            setIsBotReplying(false);

            setHistory((currentHistory) =>
                currentHistory.map((entry, index) => {
                    if (index !== currentHistory.length - 1 || entry.role !== "ai") {
                        return entry;
                    }

                    return {
                        ...entry,
                        isStreaming: false,
                    };
                })
            );

            return;
        }

        if (typeof msg === "string") {
            msg = {
                content: msg,
                role: "ai",
            };
        }

        setIsLoading(false);
        setIsBotReplying(true);

        setHistory((h) => {
            const id = msg.identifier;
            let matchIndex = -1;

            if (id) {
                for (let i = h.length - 1; i >= 0; i -= 1) {
                    if (h[i].role === "ai" && h[i].identifier === id) {
                        matchIndex = i;
                        break;
                    }
                }
            }

            if (matchIndex === -1) {
                getTokenUsed();
                setIsBotReplying(!!msg.isStreaming);

                return [
                    ...h,
                    {
                        role: "ai",
                        content: msg.content,
                        isStreaming: !!msg.isStreaming,
                        identifier: msg.identifier,
                        sessionId: msg.sessionId,
                    },
                ];
            }

            const newHistory = h.slice();

            if (!msg.isStreaming) {
                newHistory[matchIndex] = {
                    ...newHistory[matchIndex],
                    content: msg.content,
                    isStreaming: false,
                    identifier: msg.identifier,
                    sessionId: msg.sessionId,
                };

                getTokenUsed();
                setIsBotReplying(false);
            } else {
                newHistory[matchIndex] = {
                    ...newHistory[matchIndex],
                    content: msg.content,
                    isStreaming: !!msg.isStreaming,
                    identifier: msg.identifier,
                    sessionId: msg.sessionId,
                };
            }

            return newHistory;
        });
    }, [sessionId]);

    useEffect(() => {
        getTokenUsed();

        if (!socket.connected) socket.connect();

        socket.on(SOCKET_EVENTS.MCP_AGENT_INIT, getMessage);

        return () => {
            socket.off(SOCKET_EVENTS.MCP_AGENT_INIT, getMessage);
        };
    }, [getMessage]);

    useEffect(() => {
        getTokenUsed();
    }, []);

    const stopReply = async () => {
        if (!isUsableSessionId(sessionId)) return;

        try {
            await axios.post(`/api/awareness-manager/${sessionId}/stop`);
            setIsLoading(false);
            setIsBotReplying(false);
        } catch (err) {
            apiError(err);
        }
    };

    function formatTokenCount(tokenCount) {
        if (tokenCount >= 1_000_000_000) {
            return `${Math.round(tokenCount / 1_000_000_000)}B`;
        }

        if (tokenCount >= 1_000_000) {
            return `${Math.round(tokenCount / 1_000_000)}M`;
        }

        if (tokenCount >= 1_000) {
            return `${Math.round(tokenCount / 1_000)}K`;
        }

        return `${tokenCount}`;
    }

    const hasWideAiMessage = history.some(
        (m) => m.role === "ai" && aiMessageShouldUseFullWidth(m.content || "")
    );

    const latestUserMessageIndex = useMemo(() => {
        for (let i = history.length - 1; i >= 0; i -= 1) {
            if (history[i]?.role === "user") return i;
        }

        return -1;
    }, [history]);

    const trimmedHistorySearch = historySearch.trim().toLowerCase();

    const visibleChatSessions = useMemo(() => {
        if (!trimmedHistorySearch) return chatSessions;

        return chatSessions.filter((chatSession) => {
            const searchableText = [
                chatSession.title,
                getHistoryPreviewText(chatSession.preview),
                formatHistoryDate(chatSession.updatedAt || chatSession.createdAt),
            ]
                .map(cleanHistoryText)
                .join(" ")
                .toLowerCase();

            return searchableText.includes(trimmedHistorySearch);
        });
    }, [chatSessions, trimmedHistorySearch]);

    // The campaign draft preview panel opens on the right when an AI reply
    // contains a CLP_ id. Holds the active CLP id, or null when closed.
    const [activeCampaignDraftPreviewId, setActiveCampaignDraftPreviewId] = useState(null);
    const [isLaunchingCampaign, setIsLaunchingCampaign] = useState(false);
    const autoOpenedCampaignDraftPreviewIdsRef = useRef(new Set());
    const previousRightPanelSessionIdRef = useRef(sessionId);
    const showCampaignDraftPreview = !!activeCampaignDraftPreviewId;

    // The template preview panel opens on the right after the user clicks a
    // "Preview Template" button (rendered when an AI reply contains a CTP_ id).
    // Holds the active campaignTemplateId, or null when closed.
    const [activeTemplatePreviewId, setActiveTemplatePreviewId] = useState(null);
    const showTemplatePreview = !!activeTemplatePreviewId;

    // The create-group panel opens on the right when the user clicks the
    // "Create Group" button inside a GroupListSelection component.
    const [showCreateGroupPanel, setShowCreateGroupPanel] = useState(false);

    // The create-template panel opens on the right when the user clicks the
    // "Create Template" button inside an AiTemplateSelection component. Holds the
    // attack-type / credential-harvesting context for the template editor, or
    // null when closed.
    const [createTemplateContext, setCreateTemplateContext] = useState(null);
    const showCreateTemplatePanel = !!createTemplateContext;

    const showRightPanel = showCampaignDraftPreview || showCreateGroupPanel || showCreateTemplatePanel || showTemplatePreview;

    const closeLeftSideMenu = useCallback(() => {
        setIsHistoryOpen(false);
        if (onCollapseSidebar) onCollapseSidebar();
    }, [onCollapseSidebar]);

    const closeRightPanel = useCallback(() => {
        setActiveCampaignDraftPreviewId(null);
        setShowCreateGroupPanel(false);
        setCreateTemplateContext(null);
    }, []);

    // Only one right-side drawer may be open at a time. Each opener closes the
    // others so opening any drawer replaces whatever was previously shown.
    const openCampaignDraftPreview = useCallback((clpId) => {
        closeLeftSideMenu();
        setShowCreateGroupPanel(false);
        setCreateTemplateContext(null);
        setActiveTemplatePreviewId(null);
        setActiveCampaignDraftPreviewId(clpId);
    }, [closeLeftSideMenu]);

    const openCreateGroupPanel = useCallback(() => {
        closeLeftSideMenu();
        setActiveCampaignDraftPreviewId(null);
        setCreateTemplateContext(null);
        setActiveTemplatePreviewId(null);
        setShowCreateGroupPanel(true);
    }, [closeLeftSideMenu]);

    const openCreateTemplatePanel = useCallback((context) => {
        closeLeftSideMenu();
        setShowCreateGroupPanel(false);
        setActiveCampaignDraftPreviewId(null);
        setActiveTemplatePreviewId(null);
        setCreateTemplateContext(context);
    }, [closeLeftSideMenu]);

    useEffect(() => {
        if (showRightPanel) closeLeftSideMenu();
    }, [showRightPanel, closeLeftSideMenu]);

    useEffect(() => {
        if (previousRightPanelSessionIdRef.current === sessionId) return;

        previousRightPanelSessionIdRef.current = sessionId;
        autoOpenedCampaignDraftPreviewIdsRef.current.clear();
        closeRightPanel();
    }, [sessionId, closeRightPanel]);

    useEffect(() => {
        const lastMessage = history[history.length - 1];
        const clpId =
            lastMessage?.role === "ai" && !lastMessage.isStreaming
                ? extractClpPreviewId(lastMessage.content)
                : null;

        if (!clpId || autoOpenedCampaignDraftPreviewIdsRef.current.has(clpId)) return;

        autoOpenedCampaignDraftPreviewIdsRef.current.add(clpId);
        openCampaignDraftPreview(clpId);
    }, [history, openCampaignDraftPreview]);

    const openTemplatePreview = useCallback((templateId) => {
        setShowCreateGroupPanel(false);
        setCreateTemplateContext(null);
        setActiveCampaignDraftPreviewId(null);
        setActiveTemplatePreviewId(templateId);
    }, []);

    const [panelWidth, setPanelWidth] = useState(DEFAULT_RIGHT_PANEL_WIDTH);
    const panelRef = useRef(null);

    const getPanelBounds = useCallback(() => {
        const isTemplatePanel = showCreateTemplatePanel;
        const minWidth = isTemplatePanel ? TEMPLATE_RIGHT_PANEL_MIN_WIDTH : DEFAULT_RIGHT_PANEL_MIN_WIDTH;
        const maxWidth = getViewportPanelMaxWidth(
            isTemplatePanel ? TEMPLATE_RIGHT_PANEL_MAX_WIDTH : DEFAULT_RIGHT_PANEL_MAX_WIDTH,
            minWidth
        );

        return {
            minWidth,
            maxWidth,
            preferredWidth: isTemplatePanel ? TEMPLATE_RIGHT_PANEL_WIDTH : DEFAULT_RIGHT_PANEL_WIDTH,
        };
    }, [showCreateTemplatePanel]);

    useEffect(() => {
        if (!showRightPanel) return;

        const { minWidth, maxWidth, preferredWidth } = getPanelBounds();
        setPanelWidth(clampPanelWidth(preferredWidth, minWidth, maxWidth));
    }, [showRightPanel, showCreateTemplatePanel, showCreateGroupPanel, showCampaignDraftPreview, getPanelBounds]);

    useEffect(() => {
        if (!showRightPanel) return undefined;

        const handleResize = () => {
            const { minWidth, maxWidth } = getPanelBounds();
            setPanelWidth((currentWidth) => clampPanelWidth(currentWidth, minWidth, maxWidth));
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [showRightPanel, getPanelBounds]);

    const startPanelResize = useCallback((e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = panelRef.current ? panelRef.current.offsetWidth : panelWidth;
        const { minWidth, maxWidth } = getPanelBounds();
        let rafId = null;

        const onMouseMove = (moveEvt) => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const delta = startX - moveEvt.clientX;
                const next = clampPanelWidth(startWidth + delta, minWidth, maxWidth);
                // Directly set DOM width — no React re-render during drag
                if (panelRef.current) {
                    panelRef.current.style.width = `${next}px`;
                    // Also update the CSS variable so mcp-content-area adjusts
                    const container = panelRef.current.closest('.chat-container');
                    if (container) container.style.setProperty('--mcp-panel-width', `${next}px`);
                }
            });
        };
        const onMouseUp = (upEvt) => {
            if (rafId) cancelAnimationFrame(rafId);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            // Commit final width to React state once
            const delta = startX - upEvt.clientX;
            const final = clampPanelWidth(startWidth + delta, minWidth, maxWidth);
            setPanelWidth(final);
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [panelWidth, getPanelBounds]);

    const launchCampaign = useCallback(async (payload) => {
        if (!payload || isLaunchingCampaign) return;

        setIsLaunchingCampaign(true);
        try {
            // Sync the updated payload back to mcp_metadata before creating the campaign
            // so the CLP_ draft stays consistent with what was actually launched.
            const clpId = payload.campaignPreviewId || activeCampaignDraftPreviewId;
            if (clpId) {
                try {
                    await axios.put(`/api/simulation/draft-campaign/${encodeURIComponent(clpId)}`, payload);
                } catch (updateErr) {
                    console.warn("Could not update campaign draft preview metadata:", updateErr);
                    // Non-fatal — proceed with campaign creation anyway.
                }
            }

            const response = await axios.post("/api/simulation/", payload);

            setActiveCampaignDraftPreviewId(null);

            const campaignId = response.data?.campaignId;
            const isTest = payload.campaignType === "test";
            const campaignName = payload.campaignName || "Your campaign";

            const canRedirect = !!campaignId && !!routerHistory?.push;
            const willView = await customSwal({
                title: isTest ? "Test Campaign Launched!" : "Campaign Launched!",
                description: `<strong>${campaignName}</strong> has been launched successfully${
                    canRedirect ? " and is now live." : "."
                }`,
                icon: "success",
                buttonText: canRedirect ? "View Campaign" : "Done",
                showCancel: canRedirect,
                cancelButtonText: "Stay Here",
            });

            if (willView && canRedirect) {
                routerHistory.push(`/campaign/${campaignId}`);
            }
            return response.data;
        } catch (err) {
            apiError(err);
        } finally {
            setIsLaunchingCampaign(false);
        }
    }, [isLaunchingCampaign, routerHistory, activeCampaignDraftPreviewId]);

    return (
        <div
            className={`chat-container d-flex flex-column vh-100 ${
                showRightPanel ? "mcp-has-panel" : ""
            } ${showCreateTemplatePanel ? "mcp-has-template-panel" : ""}`}
            style={showRightPanel ? { '--mcp-panel-width': `${panelWidth}px` } : undefined}
        >
            <div
                className={`mcp-history-backdrop ${isHistoryOpen ? "show" : ""}`}
                onClick={() => setIsHistoryOpen(false)}
            />

            <aside className={`mcp-history-drawer ${isHistoryOpen ? "open" : ""}`}>
                <div className="mcp-history-drawer-header">
                    <div>
                        <span className="mcp-history-kicker">Threatcop AI</span>
                        <h5>Chat History</h5>
                        <p>Search and reopen your most recent saved conversations.</p>
                    </div>

                    <button
                        type="button"
                        className="mcp-history-close"
                        onClick={() => setIsHistoryOpen(false)}
                        aria-label="Close chat history"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="mcp-history-drawer-body">
                    {historyError && (
                        <div className="mcp-history-alert">{historyError}</div>
                    )}

                    <div className="mcp-history-search">
                        <Search size={15} />
                        <input
                            type="search"
                            value={historySearch}
                            onChange={(event) => setHistorySearch(event.target.value)}
                            placeholder="Search recent chats"
                            aria-label="Search recent chats"
                        />
                    </div>

                    {hasMoreChatSessions && (
                        <div className="mcp-history-retention-note">
                            Only your 7 most recent chats are kept here.
                        </div>
                    )}

                    {isHistoryLoading && chatSessions.length === 0 && (
                        <div className="mcp-history-skeleton-wrap">
                            <span className="mcp-history-skeleton" />
                            <span className="mcp-history-skeleton short" />
                            <span className="mcp-history-skeleton" />
                        </div>
                    )}

                    {!isHistoryLoading && chatSessions.length === 0 && (
                        <div className="mcp-history-empty">
                            <MessageSquare size={28} />
                            <h6>No previous chats yet</h6>
                            <p>Your saved conversations will appear here once you start chatting.</p>
                        </div>
                    )}

                    {!isHistoryLoading && chatSessions.length > 0 && visibleChatSessions.length === 0 && (
                        <div className="mcp-history-empty">
                            <Search size={28} />
                            <h6>No matching chats</h6>
                            <p>Try a different keyword from the latest 7 saved chats.</p>
                        </div>
                    )}

                    {visibleChatSessions.map((chatSession) => {
                        const active = chatSession.sessionId === sessionId;
                        const opening = loadingSessionId === chatSession.sessionId;
                        const title = cleanHistoryText(chatSession.title) || "New Conversation";
                        const isCustomReportPreview = !!extractCrpPreviewId(chatSession.preview);
                        const preview = getHistoryPreviewText(chatSession.preview);

                        return (
                            <button
                                type="button"
                                key={chatSession.sessionId}
                                className={`mcp-history-item ${active ? "active" : ""}`}
                                onClick={() => openChatSession(chatSession.sessionId)}
                                disabled={isLoading || isBotReplying || opening}
                            >
                                <span className="mcp-history-item-icon">
                                    <MessageSquare size={16} />
                                </span>

                                <span className="mcp-history-item-content">
                                    <span className="mcp-history-item-title">{title}</span>

                                    {isCustomReportPreview ? (
                                        <span className="mcp-history-report-preview">
                                            <span className="mcp-history-report-preview-icon">
                                                <span />
                                                <span />
                                                <span />
                                            </span>
                                            <span>{preview}</span>
                                        </span>
                                    ) : preview && (
                                        <span className="mcp-history-item-preview">{preview}</span>
                                    )}

                                    <span className="mcp-history-item-meta">
                                        {opening
                                            ? "Opening..."
                                            : formatHistoryDate(chatSession.updatedAt || chatSession.createdAt)}
                                        {active ? " - Current" : ""}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </aside>

            <div className="mcp-body-row">
            <div className="mcp-content-area">
                <div className="">
                    <div
                        className={`chat-messages py-4 col-sm-12 ${
                            hasWideAiMessage ? "col-12 mcp-chat-wide" : "col-lg-12"
                        }`}
                    >
                        <Container fluid className="mcp-chat-container">
                            <Row>
                                <div className="col-9 mcp-chat-inner">
                                    {history.length === 0 && !isLoading && (
                                        <div className="py-4">
                                            <div className="welcome-icon mb-4">
                                                <PiSparkleBold size={48} className="text-primary" />
                                            </div>

                                            <h1
                                                className="mb-3 fw-bold"
                                                style={{ fontSize: "35px", color: "#1f2937" }}
                                            >
                                                {isAIReport ? (
                                                    <>
                                                        Threatcop AI <br /> is ready to build your report.
                                                    </>
                                                ) : (
                                                    "Welcome to Threatcop AI"
                                                )}
                                            </h1>

                                            <p className="text-muted mb-5 fs-10">
                                                {isAIReport
                                                    ? "Not sure what to ask? Start with a suggestion or type what report you'd like to generate."
                                                    : "Chat with our cybersecurity AI assistant — find vulnerable users, assess risk levels, and launch targeted awareness campaigns."}
                                            </p>

                                            {isAIReport ? (
                                                <div className="d-flex gap-3">
                                                    <div
                                                        className="report-suggestion-item"
                                                        onClick={() => sendMessage("Campagin with click rate above 25% this month.")}
                                                    >
                                                        <p className="report-suggestion-item-text">
                                                            Campaign with click rate above 25% this month.
                                                        </p>
                                                        <span className="report-suggestion-item-icon">
                                                            <PiArrowSquareOut />
                                                        </span>
                                                    </div>

                                                    <div
                                                        className="report-suggestion-item"
                                                        onClick={() => sendMessage("User with the highest repeat failure count.")}
                                                    >
                                                        <p className="report-suggestion-item-text">
                                                            User with the highest repeat failure count.
                                                        </p>
                                                        <span className="report-suggestion-item-icon">
                                                            <PiArrowSquareOut />
                                                        </span>
                                                    </div>

                                                    <div
                                                        className="report-suggestion-item"
                                                        onClick={() => sendMessage("Which department has the highest failure rate.")}
                                                    >
                                                        <p className="report-suggestion-item-text">
                                                            Which department has the highest failure rate.
                                                        </p>
                                                        <span className="report-suggestion-item-icon">
                                                            <PiArrowSquareOut />
                                                        </span>
                                                    </div>

                                                    <div
                                                        className="report-suggestion-item"
                                                        onClick={() => sendMessage("User who click on all Simulations.")}
                                                    >
                                                        <p className="report-suggestion-item-text">
                                                            User who click on all Simulations.
                                                        </p>
                                                        <span className="report-suggestion-item-icon">
                                                            <PiArrowSquareOut />
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="d-flex gap-3">
                                                    <div
                                                        className="report-suggestion-item"
                                                        onClick={() => sendMessage("Which employees in my organization are the most vulnerable?")}
                                                    >
                                                        <p className="report-suggestion-item-text">
                                                            Which employees in my organization are the most vulnerable?
                                                        </p>
                                                        <span className="report-suggestion-item-icon">
                                                            <PiArrowSquareOut />
                                                        </span>
                                                    </div>

                                                    <div
                                                        className="report-suggestion-item"
                                                        onClick={() => sendMessage("Which campaign received the highest number of user data submissions?")}
                                                    >
                                                        <p className="report-suggestion-item-text">
                                                            Which campaign received the highest number of user data submissions?
                                                        </p>
                                                        <span className="report-suggestion-item-icon">
                                                            <PiArrowSquareOut />
                                                        </span>
                                                    </div>

                                                    <div
                                                        className="report-suggestion-item"
                                                        onClick={() => sendMessage("Which attack vector is the most vulnerable based on the submitted data?")}
                                                    >
                                                        <p className="report-suggestion-item-text">
                                                            Which attack vector is the most vulnerable based on the submitted data?
                                                        </p>
                                                        <span className="report-suggestion-item-icon">
                                                            <PiArrowSquareOut />
                                                        </span>
                                                    </div>

                                                    <div
                                                        className="report-suggestion-item"
                                                        onClick={() => sendMessage("Who are the most frequent repeat offenders behind phishing attack vectors?")}
                                                    >
                                                        <p className="report-suggestion-item-text">
                                                            Who are the most frequent repeat offenders behind phishing attack vectors?
                                                        </p>
                                                        <span className="report-suggestion-item-icon">
                                                            <PiArrowSquareOut />
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="d-flex flex-column gap-4">
                                        {history.map((msg, i) => (
                                            <div
                                                key={i}
                                                ref={i === latestUserMessageIndex ? latestUserMessageRef : null}
                                                className={`${
                                                    msg.role === "ai" && i === history.length - 1
                                                        ? "ai-replying"
                                                        : ""
                                                }`}
                                            >
                                                <div
                                                    className={`d-flex gap-3 w-100 ${
                                                        msg.role === "user" ? "flex-row-reverse" : ""
                                                    }`}
                                                >
                                                    <div
                                                        className={`${
                                                            msg.role === "user" ? "" : "ai-avatar"
                                                        } d-flex align-items-center justify-content-center text-white flex-shrink-0`}
                                                        style={{
                                                            width: "40px",
                                                            height: "40px",
                                                            borderRadius: "50%",
                                                        }}
                                                    >
                                                        {msg.role === "user" ? (
                                                            ""
                                                        ) : (
                                                            <img
                                                                src={ThreatcopAiIcon}
                                                                alt="Threatcop AI"
                                                                style={{ width: "100%" }}
                                                            />
                                                        )}
                                                    </div>

                                                    <Card
                                                        className={`${
                                                            msg.role === "user"
                                                                ? "user-message"
                                                                : `ai-message${
                                                                    aiMessageShouldUseFullWidth(msg.content || "")
                                                                        ? " ai-message-wide"
                                                                        : ""
                                                                }`
                                                        }`}
                                                    >
                                                        <Card.Body className="msgSentWrapper">
                                                            <div>
                                                                {msg.role === "ai" ? (() => {
                                                                    const parsedBlock = !msg.isStreaming
                                                                        ? tryParseAiJson(msg.content)
                                                                        : {
                                                                            json: null,
                                                                            beforeHtml: "",
                                                                            afterHtml: "",
                                                                            rawJson: null,
                                                                        };

                                                                    const parsed = parsedBlock?.json;

                                                                    const wantsTemplateStep =
                                                                        !msg.isStreaming &&
                                                                        (parsed?.jsonId === "fetch-templates-for-campaign-selection" ||
                                                                            (typeof msg.content === "string" &&
                                                                                msg.content.includes("fetch-templates-for-campaign-selection")));

                                                                    if (wantsTemplateStep) {
                                                                        return (
                                                                            <>
                                                                                {parsedBlock.beforeHtml && (
                                                                                    <AiMessageRenderer
                                                                                        content={parsedBlock.beforeHtml}
                                                                                        isStreaming={false}
                                                                                    />
                                                                                )}

                                                                                {parsed?.reply && (
                                                                                    <p className="mcp-ai-structured-reply">
                                                                                        {parsed.reply}
                                                                                    </p>
                                                                                )}

                                                                                <div className="mcp-ai-component">
                                                                                <AiTemplateSelection
                                                                                    templateId={parsed?.templateId}
                                                                                    onPreviewTemplate={openTemplatePreview}
                                                                                    selectedCategory={parsed?.selectedCategory}
                                                                                    selectedCategoryIds={parsed?.selectedCategoryIds}
                                                                                    selectedTemplateIds={parsed?.selectedTemplateIds}
                                                                                    templateMode={parsed?.templateMode}
                                                                                    simulationType={parsed?.simulationType || "Simulation Attack"}
                                                                                    attackType={parsed?.attackType || "Phishing"}
                                                                                    credentialHarvesting={parsed?.credentialHarvesting}
                                                                                    campaignDetails={parsed?.campaignDetails}
                                                                                    campaignId={parsed?.campaignId}
                                                                                    simulationId={parsed?.simulationId}
                                                                                    targetInteractionType={parsed?.targetInteractionType}
                                                                                    isAIbased={parsed?.isAIbased}
                                                                                    onDone={(selectedTemplates) => {
                                                                                            const templates = Array.isArray(selectedTemplates)
                                                                                                ? selectedTemplates
                                                                                                : [];
                                                                                            const selectedTemplateNames = templates
                                                                                                .map((tpl) => `${tpl.name || tpl.title || "Template"} (${tpl.id})`)
                                                                                                .join(", ");

                                                                                            if (selectedTemplateNames) {
                                                                                                sendMessage(
                                                                                                    `I have selected the following templates for the campaign: ${selectedTemplateNames}`
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        onCreateTemplate={(context = {}) => {
                                                                                            // Replace whatever is in the right panel with the template editor.
                                                                                            openCreateTemplatePanel({
                                                                                                attackType: context.attackType ?? parsed?.attackType ?? "Phishing",
                                                                                                simulationType: context.simulationType ?? parsed?.simulationType ?? "Simulation Attack",
                                                                                                credentialHarvesting: context.credentialHarvesting ?? parsed?.credentialHarvesting ?? false,
                                                                                                targetInteractionType: context.targetInteractionType ?? parsed?.targetInteractionType ?? "",
                                                                                                isAIbased: context.isAIbased ?? parsed?.isAIbased ?? false,
                                                                                                templateId: context.campaignTemplateId ?? context.templateId ?? null,
                                                                                                selectedTemplateIds: [],
                                                                                            });
                                                                                        }}
                                                                                />
                                                                                </div>

                                                                                {parsed?.footer && (
                                                                                    <p className="mcp-ai-structured-footer">
                                                                                        {parsed.footer}
                                                                                    </p>
                                                                                )}

                                                                                {parsedBlock.afterHtml && (
                                                                                    <AiMessageRenderer
                                                                                        content={parsedBlock.afterHtml}
                                                                                        isStreaming={false}
                                                                                    />
                                                                                )}
                                                                            </>
                                                                        );
                                                                    }

                                                                    if (parsed?.jsonId === "simulation-attack-credential-for-campaign") {
                                                                        return (
                                                                            <>
                                                                                {/* {parsedBlock?.beforeHtml && (
                                                                                    <AiMessageRenderer
                                                                                        content={parsedBlock.beforeHtml}
                                                                                        isStreaming={false}
                                                                                    />
                                                                                )}

                                                                                {parsed?.reply && (
                                                                                    <p className="mcp-ai-structured-reply">
                                                                                        {parsed.reply}
                                                                                    </p>
                                                                                )} */}

                                                                                <div className="mcp-ai-component">
                                                                                <AiSimulationAttack
                                                                                    onDone={({ mode, type, typeLabel, credentialHarvesting, targetInteractionType, isAIbased }) => {
                                                                                        const modeLabel =
                                                                                            mode === "simulation+training"
                                                                                                ? "Simulation Attack + Training"
                                                                                                : "Simulation Attack";

                                                                                        // Vishing/WhatsApp are also judged via targetInteractionType + isAIbased.
                                                                                        let interactionPart = "";
                                                                                        if (type === "Vishing" || type === "WhatsApp") {
                                                                                            interactionPart =
                                                                                                ` The target interaction type is ${targetInteractionType} and it is ${isAIbased ? "AI-based (AI Conversational)" : "not AI-based"}.`;
                                                                                        }

                                                                                        sendMessage(
                                                                                            `I want to run a ${modeLabel} campaign with attack type ${typeLabel} and credential harvesting ${credentialHarvesting ? "true" : "false"}.${interactionPart}`
                                                                                        );
                                                                                    }}
                                                                                />
                                                                                </div>

                                                                                {parsed?.footer && (
                                                                                    <p className="mcp-ai-structured-footer">
                                                                                        {parsed.footer}
                                                                                    </p>
                                                                                )}

                                                                                {parsedBlock?.afterHtml && (
                                                                                    <AiMessageRenderer
                                                                                        content={parsedBlock.afterHtml}
                                                                                        isStreaming={false}
                                                                                    />
                                                                                )}
                                                                            </>
                                                                        );
                                                                    }

                                                                    if (parsed?.jsonId === "fetch-group-list-to-select") {
                                                                        return (
                                                                            <>
                                                                                {/* {parsedBlock.beforeHtml && (
                                                                                    <AiMessageRenderer
                                                                                        content={parsedBlock.beforeHtml}
                                                                                        isStreaming={false}
                                                                                    />
                                                                                )}

                                                                                {parsed.reply && (
                                                                                    <p className="mcp-ai-structured-reply">
                                                                                        {parsed.reply}
                                                                                    </p>
                                                                                )} */}

                                                                                <div className="mcp-ai-component">
                                                                                <GroupListSelection
                                                                                    groupList={
                                                                                        Array.isArray(parsed.groupList)
                                                                                            ? parsed.groupList
                                                                                            : undefined
                                                                                    }
                                                                                    onDone={(selectedIds, selectedGroups) => {
                                                                                        if (!selectedIds || selectedIds.length === 0) return;

                                                                                        const groupsForMessage = Array.isArray(selectedGroups)
                                                                                            ? selectedGroups
                                                                                            : [];

                                                                                        const selectedGroupNames = groupsForMessage
                                                                                            .map((group) => `${group.name} (${group.id})`)
                                                                                            .join(", ");

                                                                                        sendMessage(
                                                                                            selectedGroupNames
                                                                                                ? `I have selected the following groups for the campaign: ${selectedGroupNames}`
                                                                                                : `I have selected the following group IDs for the campaign: ${selectedIds.join(", ")}`
                                                                                        );
                                                                                    }}
                                                                                    onCreateGroup={() => {
                                                                                        openCreateGroupPanel();
                                                                                    }}
                                                                                />
                                                                                </div>

                                                                                {parsed.footer && (
                                                                                    <p className="mcp-ai-structured-footer">
                                                                                        {parsed.footer}
                                                                                    </p>
                                                                                )}

                                                                                {parsedBlock.afterHtml && (
                                                                                    <AiMessageRenderer
                                                                                        content={parsedBlock.afterHtml}
                                                                                        isStreaming={false}
                                                                                    />
                                                                                )}
                                                                            </>
                                                                        );
                                                                    }

                                                                    const clpPreviewId = !msg.isStreaming
                                                                        ? extractClpPreviewId(msg.content)
                                                                        : null;

                                                                    if (clpPreviewId) {
                                                                        const cleanedContent = stripClpPreviewId(msg.content);

                                                                        return (
                                                                            <>
                                                                                {cleanedContent && (
                                                                                    <AiMessageRenderer
                                                                                        content={cleanedContent}
                                                                                        isStreaming={false}
                                                                                    />
                                                                                )}

                                                                                <button
                                                                                    type="button"
                                                                                    className="mcp-campaign-preview-btn btn-primary"
                                                                                    onClick={() => {
                                                                                        openCampaignDraftPreview(clpPreviewId);
                                                                                    }}
                                                                                >
                                                                                    <PiArrowSquareOut />
                                                                                    <span>Campaign Launch Preview</span>
                                                                                </button>
                                                                            </>
                                                                        );
                                                                    }

                                                                    const ctpPreviewId = !msg.isStreaming
                                                                        ? extractCtpPreviewId(msg.content)
                                                                        : null;

                                                                    if (ctpPreviewId) {
                                                                        const cleanedContent = stripCtpPreviewId(msg.content);

                                                                        return (
                                                                            <>
                                                                                {cleanedContent && (
                                                                                    <AiMessageRenderer
                                                                                        content={cleanedContent}
                                                                                        isStreaming={false}
                                                                                    />
                                                                                )}

                                                                                <button
                                                                                    type="button"
                                                                                    className="mcp-campaign-preview-btn btn-primary"
                                                                                    onClick={() => {
                                                                                        // Open the full editable template wizard (Email /
                                                                                        // Landing / Awareness / Attacker / Preview tabs with
                                                                                        // "Edit with prompt") — same view as the
                                                                                        // template-selection step. AiCampaignTemplate.load()
                                                                                        // self-hydrates attackType / simulationType /
                                                                                        // credentialHarvesting and all step ids from
                                                                                        // /api/campaign/template/{id}, so the templateId alone
                                                                                        // is sufficient.
                                                                                        openCreateTemplatePanel({
                                                                                            templateId: ctpPreviewId,
                                                                                            selectedTemplateIds: [],
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    <PiArrowSquareOut />
                                                                                    <span>Preview Template</span>
                                                                                </button>
                                                                            </>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <AiMessageRenderer
                                                                            content={msg.content || ""}
                                                                            isStreaming={!!msg.isStreaming}
                                                                        />
                                                                    );
                                                                })() : (
                                                                    <span>{msg.content}</span>
                                                                )}
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            </div>
                                        ))}

                                        {(isLoading || showLoadingPlaceholder) && (
                                            <div
                                                className="d-flex gap-3 ai-replying"
                                                style={!isLoading ? { visibility: "hidden" } : undefined}
                                                aria-hidden={!isLoading}
                                            >
                                                <div
                                                    className="ai-avatar d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                                    style={{
                                                        width: "40px",
                                                        height: "40px",
                                                        borderRadius: "50%",
                                                    }}
                                                >
                                                    <Bot size={18} />
                                                </div>

                                                <span className="loadingText">
                                                    <LoadingDots width="500px" />
                                                </span>
                                            </div>
                                        )}

                                        <div ref={bottomRef} />
                                    </div>
                                </div>
                            </Row>
                        </Container>
                    </div>
                </div>

                <div className="glass-footer p-4">
                    <Container fluid className="mcp-chat-container">
                        <Row>
                            <div className="col-9 mcp-chat-inner">
                                <div className="report-ai-footer">
                                    <Form onSubmit={sendMessage}>
                                        <div className="report-ai-footer-input">
                                            <Form.Control
                                                ref={messageInputRef}
                                                as="textarea"
                                                rows={2}
                                                placeholder="Enter your prompt here..."
                                                value={input}
                                                onChange={(e) => setInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                        e.preventDefault();
                                                        sendMessage(e);
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className="report-ai-footer-item">
                                            <p>
                                                <img
                                                    src={Coin}
                                                    alt="Coin"
                                                    className="report-ai-footer-item-coin"
                                                />
                                                <span className="report-ai-footer-item-token-used">
                                                    {formatTokenCount(tokenLeft)}
                                                </span>
                                                &nbsp;tokens left
                                            </p>

                                            <div className="d-inline-flex align-items-center mcp-footer-send-wrap">
                                                {isBotReplying ? (
                                                    <Button
                                                        variant="primary"
                                                        type="button"
                                                        className="mr-2 mcp-footer-send-btn"
                                                        onClick={stopReply}
                                                        disabled={!isLoading && !isBotReplying}
                                                    >
                                                        <FaStop />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="primary"
                                                        type="submit"
                                                        className="mcp-footer-send-btn"
                                                        disabled={isLoading || isBotReplying || !input.trim()}
                                                        aria-label="Send message"
                                                    >
                                                        <PiArrowUpBold />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </Form>
                                </div>

                                <p
                                    className="text-muted text-center mt-3 mb-0"
                                    style={{ fontSize: "11px" }}
                                >
                                    Threatcop AI may make mistakes. Verify critical actions before executing.
                                </p>
                            </div>
                        </Row>
                    </Container>
                </div>
            </div>

                {showRightPanel && (
                    <aside
                        className={[
                            "mcp-campaign-draft-preview-panel",
                            "mcp-panel-open",
                            showCreateTemplatePanel ? "mcp-template-panel" : "",
                            showCreateGroupPanel ? "mcp-group-panel" : "",
                            showCampaignDraftPreview ? "mcp-preview-panel" : "",
                        ].filter(Boolean).join(" ")}
                        ref={panelRef}
                        style={{ width: panelWidth }}
                    >
                        <div className="mcp-panel-resize-handle" onMouseDown={startPanelResize} title="Drag to resize" />
                        {showCreateGroupPanel ? (
                            <AiCreateGroup
                                onClose={() => setShowCreateGroupPanel(false)}
                                onCreated={(group) => {
                                    setShowCreateGroupPanel(false);
                                    sendMessage(
                                        `I have selected the following groups for the campaign: "${group.name}" (${group.id})).`
                                    );
                                }}
                                onPromptCreate={(promptText) => {
                                    setShowCreateGroupPanel(false);
                                    sendMessage(
                                        `Create a group with the following criteria: ${promptText}`
                                    );
                                }}
                            />
                        ) : showCreateTemplatePanel ? (
                            createTemplateContext.templateId ? (
                                <AiCampaignTemplate
                                    attackType={createTemplateContext.attackType}
                                    simulationType={createTemplateContext.simulationType}
                                    credentialHarvesting={createTemplateContext.credentialHarvesting}
                                    targetInteractionType={createTemplateContext.targetInteractionType}
                                    isAIbased={createTemplateContext.isAIbased}
                                    templateId={createTemplateContext.templateId}
                                    selectedTemplateIds={createTemplateContext.selectedTemplateIds}
                                    nextPath={(path) => (routerHistory ? routerHistory.push(path) : (window.location.href = path))}
                                    onFinish={(template) => {
                                        setCreateTemplateContext(null);
                                        sendMessage(
                                            `I have selected this campaign template: "${template?.name || ""}" (${template?.id}).`
                                        );
                                    }}
                                    onClose={() => setCreateTemplateContext(null)}
                                />
                            ) : (
                                <TemplateCreationPrompt
                                    attackType={createTemplateContext.attackType}
                                    simulationType={createTemplateContext.simulationType}
                                    credentialHarvesting={createTemplateContext.credentialHarvesting}
                                    targetInteractionType={createTemplateContext.targetInteractionType}
                                    isAIbased={createTemplateContext.isAIbased}
                                    onTemplateCreated={(campaignTemplateId) => {
                                        setCreateTemplateContext((prev) => ({
                                            ...prev,
                                            templateId: campaignTemplateId,
                                        }));
                                    }}
                                    onClose={() => setCreateTemplateContext(null)}
                                />
                            )
                        ) : showTemplatePreview ? (
                            <div className="mcp-template-preview-panel">
                                <div className="mcp-template-preview-topbar">
                                    <span className="mcp-template-preview-title">Template Preview</span>
                                    <button
                                        type="button"
                                        className="mcp-template-preview-close"
                                        onClick={() => setActiveTemplatePreviewId(null)}
                                        title="Close"
                                        aria-label="Close"
                                    >
                                        x
                                    </button>
                                </div>
                                <div className="mcp-template-preview-body">
                                    <TemplatePreview
                                        readOnly
                                        campaignTemplateId={activeTemplatePreviewId}
                                        onClose={() => setActiveTemplatePreviewId(null)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <CampaignDraftPreview
                                campaignId={activeCampaignDraftPreviewId}
                                onClose={() => setActiveCampaignDraftPreviewId(null)}
                                onDraft={() => {}}
                                onRunCampaign={launchCampaign}
                                onViewCampaign={(launchedId) =>
                                    routerHistory
                                        ? routerHistory.push(`/campaign/${launchedId}`)
                                        : (window.location.href = `/campaign/${launchedId}`)
                                }
                                isRunningCampaign={isLaunchingCampaign}
                            />
                        )}
                    </aside>
                )}
            </div>
        </div>
    );
}

MCP.getDefaultRoute = function getDefaultRoute(search = "") {
    return createAwarenessManagerPath(getOrCreateSessionId(), search);
};
